"use client";

import { useState, useEffect } from "react";
import { PlusCircle, Search, Trash2, Edit2 } from "lucide-react";
import { FaSpinner } from "react-icons/fa";
import AddFacultyForm from "./AddFacultyForm";
import EditFacultyForm from "./EditFacultyForm";
import api from "../../../services/api";
import { toast, Toaster } from "react-hot-toast";

export default function AdminFacultyDashboard() {
  const [faculty, setFaculty] = useState([]);
  const [filteredFaculty, setFilteredFaculty] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchFaculty = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/faculty/faculty`
      );
      setFaculty(response.data);
    } catch (error) {
      console.error("Error fetching faculty:", error);
      toast.error("Failed to fetch faculty members");
    }
  };

  useEffect(() => {
    fetchFaculty();
  }, []);

  useEffect(() => {
    filterFaculty();
  }, [searchQuery, departmentFilter, faculty]);

  // Add effect to prevent scrolling when modal is open
  useEffect(() => {
    if (isAddDialogOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isAddDialogOpen]);

  const filterFaculty = () => {
    let filtered = [...faculty];

    if (departmentFilter !== "all") {
      filtered = filtered.filter(
        (member) => member.department === departmentFilter
      );
    }

    if (searchQuery) {
      filtered = filtered.filter((member) =>
        member.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredFaculty(filtered);
  };

  const handleDeleteFaculty = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this faculty member?")
    ) {
      try {
        await api.delete(`/faculty/delete-faculty/${id}`);
        toast.success("Faculty member deleted successfully");
        fetchFaculty();
      } catch (error) {
        console.error("Error deleting faculty member:", error);
        toast.error("Failed to delete faculty member");
      }
    }
  };

  const handleEditFaculty = async (member) => {
    try {
      // Fetch the complete faculty details for editing
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/faculty/faculty/${member.id}`
      );
      if (response.ok) {
        const facultyDetails = await response.json();
        setSelectedFaculty(facultyDetails);
        setIsEditDialogOpen(true);
      } else {
        console.error("Failed to fetch faculty details");
        // Fallback to using the list data
        setSelectedFaculty(member);
        setIsEditDialogOpen(true);
      }
    } catch (error) {
      console.error("Error fetching faculty details:", error);
      // Fallback to using the list data
      setSelectedFaculty(member);
      setIsEditDialogOpen(true);
    }
  };

  const handleAddFacultySuccess = () => {
    fetchFaculty();
    setIsAddDialogOpen(false);
    toast.success("Faculty member added successfully");
  };

  const handleEditFacultySuccess = () => {
    fetchFaculty();
    setIsEditDialogOpen(false);
    setSelectedFaculty(null);
    toast.success("Faculty member updated successfully");
  };

  // Button loading spinner component
  const ButtonSpinner = () => <FaSpinner className="animate-spin mr-2" />;

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6">
        <div className="bg-blue-800 text-white text-lg font-semibold px-4 py-3 rounded-t mb-6">
          Faculty Management
        </div>

        <div className="flex justify-end mb-6">
          <button
            onClick={() => setIsAddDialogOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusCircle className="h-5 w-5" />
            Add Faculty Member
          </button>
        </div>

        {/* Search and filter controls */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Search by Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search by email..."
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="department"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Filter by Department
              </label>
              <select
                id="department"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Departments</option>
                <option value="CS">Computer Science</option>
                <option value="AI">Artificial Intelligence</option>
                <option value="CyS">Cyber Security</option>
                <option value="SE">Software Engineering</option>
              </select>
            </div>
          </div>
        </div>

        {/* Modal */}
        {isAddDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
              <button
                onClick={() => setIsAddDialogOpen(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-bold mb-4">Add New Faculty Member</h2>
              <AddFacultyForm
                onSuccess={handleAddFacultySuccess}
                onCancel={() => setIsAddDialogOpen(false)}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                buttonSpinner={<ButtonSpinner />}
              />
            </div>
          </div>
        )}

        {/* Edit Faculty Modal */}
        {isEditDialogOpen && selectedFaculty && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
              <button
                onClick={() => setIsEditDialogOpen(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-bold mb-4">Edit Faculty Member</h2>
              <EditFacultyForm
                faculty={selectedFaculty}
                onSuccess={handleEditFacultySuccess}
                onCancel={() => {
                  setIsEditDialogOpen(false);
                  setSelectedFaculty(null);
                }}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                buttonSpinner={<ButtonSpinner />}
              />
            </div>
          </div>
        )}

        {/* Faculty Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>

                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredFaculty &&
                  filteredFaculty.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={`${process.env.REACT_APP_API_URL}/faculty/${member.image_url}`}
                              alt=""
                            />
                          </div>
                          <div className="ml-4">
                            <div className="font-medium text-gray-900">
                              {member.name}
                            </div>
                            <div className="text-gray-500">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {member.department}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEditFaculty(member)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit2 className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteFaculty(member.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
